import { Badge, Group, Text, Tooltip } from '@mantine/core';
import { IconRosetteDiscountCheck, IconStar, IconUser } from '@tabler/icons-react';
import { Recipe, RecipeSource } from '../../types/mealPlan';

/**
 * Per-source display copy, shared by every surface that shows provenance
 * (cards, meal plan, details, attribution footnote). A source missing from
 * this map falls back to its backend display name everywhere, so a new
 * backend adapter degrades gracefully until its copy lands here.
 *
 *  - chip: card badges. "USDA recipe", not bare "USDA": the acronym alone
 *    reads as a certification mark (USDA Organic/Prime) rather than recipe
 *    provenance.
 *  - short: the details page Source cell, whose label supplies the context
 *    the chip has to carry itself.
 *  - heading/body: the end-of-page attribution footnote.
 */
const SOURCE_COPY: Record<
  string,
  { chip: string; short: string; heading: string; body: string; tooltip: string }
> = {
  usda_myplate: {
    chip: 'USDA recipe',
    short: 'USDA',
    heading: 'USDA Recipe',
    body: 'From the USDA MyPlate Kitchen.',
    tooltip:
      "A recipe from the USDA MyPlate Kitchen, the U.S. Department of Agriculture's public collection of family-friendly recipes.",
  },
  nih_nhlbi: {
    chip: 'NIH recipe',
    short: 'NIH',
    heading: 'NIH Recipe',
    body: 'From the heart-healthy recipe collection of the National Heart, Lung, and Blood Institute.',
    tooltip:
      'A heart-healthy recipe from the National Institutes of Health, developed by the National Heart, Lung, and Blood Institute.',
  },
};

/**
 * One-sentence provenance context for source badges. Touch-enabled: the
 * badges sit outside the cards' click targets, so a tap opens the tooltip
 * on mobile without fighting navigation. No outbound link on purpose:
 * myplate.gov was retired in January 2026 and its URLs redirect.
 */
export const recipeSourceTooltip = (source: RecipeSource): string =>
  SOURCE_COPY[source.id]?.tooltip ?? `A recipe from ${source.name}.`;

/** Card badge label, e.g. "USDA recipe". */
export const recipeSourceChipLabel = (source: RecipeSource): string =>
  SOURCE_COPY[source.id]?.chip ?? source.name;

/** Details-page Source cell label, e.g. "USDA". */
export const recipeSourceShortLabel = (source: RecipeSource): string =>
  SOURCE_COPY[source.id]?.short ?? source.name;

/**
 * Shared Tooltip props for source badges so every surface renders the same
 * way. Text size matches the app's other informational tooltips (e.g. the
 * reasoning tooltip on meal plan cards), not Mantine's larger default.
 */
export const sourceTooltipProps = {
  withArrow: true,
  multiline: true,
  w: 280,
  events: { hover: true, focus: true, touch: true },
  styles: { tooltip: { fontSize: '0.75rem', lineHeight: 1.4 } },
} as const;

/**
 * Style for the badge inside a source tooltip. The tap that opens the
 * tooltip on mobile must not double as a text-selection gesture: Android
 * long-press selects the badge text and triggers the system tap-to-search
 * overlay on top of the tooltip.
 */
export const sourceBadgeStyle: React.CSSProperties = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
} as React.CSSProperties;

/**
 * Origin and source badges for recipe cards. Three axes, each with its own
 * mark, none competing for a slot:
 *
 *  - "My Recipe" (teal): recipes the user added themselves
 *  - "Top pick" (orange): curated catalog recipes the user has NOT saved.
 *    A saved top pick graduates: the user's own adoption supersedes our
 *    endorsement, so the mark disappears once it's in their cookbook.
 *  - Source (indigo, e.g. "USDA"): external provenance worth trusting
 *
 * Recipes saved from the catalog carry no origin badge at all: unmarked is
 * the default case, and provenance is only surfaced when it is a positive
 * signal (never as an AI label).
 */
export const RecipeProvenanceBadges = ({
  recipe,
  badgeStyle,
  // For surfaces that overlay the Top pick marker on the recipe image
  // (e.g. archive thumbnails) so the strip doesn't repeat it
  hideTopPick = false,
}: {
  recipe: Pick<Recipe, 'origin' | 'isCurated' | 'source' | 'isSaved'>;
  badgeStyle?: React.CSSProperties;
  hideTopPick?: boolean;
}) => (
  <>
    {recipe.origin === 'user_recipe' ? (
      <Badge
        size="sm"
        variant="light"
        color="teal"
        leftSection={<IconUser size={12} />}
        style={badgeStyle}
      >
        My Recipe
      </Badge>
    ) : recipe.isCurated && !recipe.isSaved && !hideTopPick ? (
      <Badge
        size="sm"
        variant="light"
        color="orange"
        leftSection={<IconStar size={12} />}
        style={badgeStyle}
      >
        Top pick
      </Badge>
    ) : null}
    {recipe.source && (
      <Tooltip label={recipeSourceTooltip(recipe.source)} {...sourceTooltipProps}>
        <Badge
          size="sm"
          variant="light"
          color="indigo"
          leftSection={<IconRosetteDiscountCheck size={12} />}
          style={{ ...sourceBadgeStyle, ...badgeStyle }}
        >
          {recipeSourceChipLabel(recipe.source)}
        </Badge>
      </Tooltip>
    )}
  </>
);

/**
 * End-of-page attribution line for externally sourced recipes. The card
 * chip says one word; this carries the full story where credits belong —
 * quiet footnote text, not a card. No outbound link: original myplate.gov
 * URLs redirect since the site was retired in January 2026.
 */
export const RecipeSourceAttribution = ({
  recipe,
}: {
  recipe: Pick<Recipe, 'source'>;
}) => {
  if (!recipe.source) return null;

  const copy = SOURCE_COPY[recipe.source.id];
  const heading = copy?.heading ?? recipe.source.name;
  const attribution = recipe.source.attribution
    ? recipe.source.attribution.replace(/\.?\s*$/, '.')
    : null;
  const body = copy
    ? `${copy.body}${attribution ? ` ${attribution}` : ''}`
    : attribution;

  return (
    <Group gap={6} align="flex-start" wrap="nowrap">
      <IconRosetteDiscountCheck
        size={14}
        color="var(--mantine-color-indigo-6)"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Text size="xs" c="dimmed">
        <Text span size="xs" fw={600}>
          {heading}.
        </Text>
        {body && ` ${body}`}
      </Text>
    </Group>
  );
};
